import { Fragment, type ReactNode } from 'react'
import cx from 'classnames'
import { Spinner } from '@/components/Spinner'
import styles from './CompletionToggle.module.css'
import { Icon } from '@/components/Icon'
import { isEmptyNode } from '@/utils/react-util'

interface CompletionToggleProps {
  completed?: boolean
  onPress?: () => unknown
  children?: ReactNode
  title?: string
  processing?: boolean
  readOnly?: boolean
  pulse?: boolean
  isDisabled?: boolean
  size?: number
  checkType?: CheckType
  testId?: string
}

/**
 * A round toggle that marks something as completed.
 */
export function CompletionToggle({
  completed,
  onPress,
  children,
  title,
  processing,
  readOnly,
  pulse,
  isDisabled,
  size = 24,
  testId,
  checkType = 'checkmark',
}: CompletionToggleProps) {
  const click = () => !processing && onPress?.()
  const styleProps = { width: size, height: size }
  const hasChildren = !isEmptyNode(children)
  // Read-only toggles are often placed inside another button, which can't nest a button.
  const Element = readOnly ? 'span' : 'button'
  return (
    <Element
      className={cx(styles.root, !readOnly && styles.toggleable)}
      onClick={readOnly ? undefined : click}
      title={title}
      style={styleProps}
      data-test={testId}
    >
      {processing ? (
        <Spinner thickness={2} />
      ) : (
        <Fragment>
          <div
            className={cx(styles.back, pulse && styles.pulse)}
            style={styleProps}
          />
          {completed && (
            <div className={styles.front} style={styleProps}>
              <div className={styles.content}>{children}</div>
              <span style={styleProps}>
                <Icon
                  name={checkType}
                  size={size}
                  className={cx(
                    styles[checkType],
                    !hasChildren && styles.enlarged,
                    isDisabled && styles.disabled,
                  )}
                />
              </span>
            </div>
          )}
        </Fragment>
      )}
    </Element>
  )
}

export type CheckType = 'tick' | 'checkmark'
